import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import type { SubagentTimelineItem } from "@/hooks/useChat";

interface WorkingBubbleProps {
  show?: boolean;
  status?: string;
  subagents?: SubagentTimelineItem[];
}

const DEFAULT_LABEL = "Working\u2026";

const statusLabel = (status?: string) =>
  status && status.trim() ? status : DEFAULT_LABEL;

const childrenFor = (
  subagents: SubagentTimelineItem[],
  parentId: number | null,
) => {
  const activeIds = new Set(subagents.map((agent) => agent.agentId));
  return subagents.filter((agent) =>
    parentId == null
      ? agent.parentId == null ||
        agent.parentId === 0 ||
        !activeIds.has(agent.parentId)
      : agent.parentId === parentId,
  );
};

const SubagentBranch = ({
  subagents,
  parentId,
  depth = 0,
}: {
  subagents: SubagentTimelineItem[];
  parentId: number | null;
  depth?: number;
}) => {
  const children = childrenFor(subagents, parentId);
  if (children.length === 0) return null;

  return (
    <div
      className={cn(
        "subagent-working-branch ml-5 mt-2 space-y-2 border-l pl-3",
        depth > 0 && "ml-4",
      )}
    >
      {children.map((agent) => (
        <div key={agent.agentId}>
          <div className="relative flex justify-start">
            <span className="subagent-working-connector absolute -left-3 top-1/2 h-px w-3" />
            <div className="subagent-working-bubble max-w-full rounded-xl border px-3 py-2">
              <div className="flex min-w-0 items-center gap-2">
                <Loader2 className="working-spinner h-3.5 w-3.5 shrink-0 animate-spin text-[#F59E0B]" />
                <span className="working-shimmer min-w-0 truncate text-[12px]">
                  {statusLabel(agent.workingStatus)}
                </span>
              </div>
            </div>
          </div>
          <SubagentBranch
            subagents={subagents}
            parentId={agent.agentId}
            depth={depth + 1}
          />
        </div>
      ))}
    </div>
  );
};

export const WorkingBubble = ({ show, status, subagents = [] }: WorkingBubbleProps) => {
  if (!show) return null;
  const label = statusLabel(status);
  return (
    <div className="flex justify-start">
      <div className="max-w-full">
        <div className="working-bubble rounded-2xl border border-[rgba(118,120,227,0.12)] px-4 py-3">
          <div className="flex items-center gap-2">
            <Loader2 className="working-spinner h-4 w-4 shrink-0 animate-spin text-[#7678E3]" />
            <span className="working-shimmer min-w-0 truncate text-[13px]">
              {label}
            </span>
          </div>
        </div>
        <SubagentBranch subagents={subagents} parentId={null} />
      </div>
    </div>
  );
};
